import React from "react";
import {
  Box,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Skeleton,
} from "@mui/material";
import { HiPencil, HiTrash } from "react-icons/hi";
import type { UserDto } from "@/api/types/dtos";

interface UserRowProps {
  user: UserDto;
  onEdit: (user: UserDto) => void;
  onDelete: (user: UserDto) => void;
}

export const UserRow: React.FC<UserRowProps> = ({ user, onEdit, onDelete }) => {
  const fullName = [user.name, user.lastName].filter(Boolean).join(" ");

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {fullName || "—"}
        </Typography>
        {user.email && (
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {user.idNumber || "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={user.isActive ? "Activo" : "Inactivo"}
          color={user.isActive ? "success" : "default"}
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEdit(user)} aria-label={`Editar ${fullName}`}>
              <HiPencil size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(user)}
              aria-label={`Eliminar ${fullName}`}
            >
              <HiTrash size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

interface UsersTableBodyProps {
  isLoading: boolean;
  users: UserDto[];
  debouncedSearch: string;
  skeletonRows: number;
  onEdit: (user: UserDto) => void;
  onDelete: (user: UserDto) => void;
  onCreateClick: () => void;
}

const UsersTableBody: React.FC<UsersTableBodyProps> = ({
  isLoading,
  users,
  debouncedSearch,
  skeletonRows,
  onEdit,
  onDelete,
  onCreateClick,
}) => {
  if (isLoading) {
    return (
      <TableBody>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <TableRow key={`skeleton-${i}`}>
            <TableCell>
              <Skeleton />
            </TableCell>
            <TableCell>
              <Skeleton width={120} />
            </TableCell>
            <TableCell>
              <Skeleton width={80} />
            </TableCell>
            <TableCell>
              <Skeleton width={120} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  }

  if (users.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {debouncedSearch
                ? "No se encontraron usuarios con ese criterio de búsqueda"
                : "No hay usuarios registrados"}
            </Typography>
            {!debouncedSearch && (
              <Typography
                component="button"
                variant="body2"
                onClick={onCreateClick}
                sx={{
                  mt: 1,
                  background: "none",
                  border: "none",
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  font: "inherit",
                }}
              >
                Crear primer usuario
              </Typography>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {users.map((user) => (
        <UserRow key={user.id} user={user} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </TableBody>
  );
};

export default UsersTableBody;
